'use client'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { addMonths, clampMonth, monthOptions, monthWindow, yearOptions } from './_lib/bookingCalendarModel'

type Props = {
  /** Anchor month 'YYYY-MM-01' (the left month in dual-month layouts). */
  monthStart: string
  /** Inclusive 'YYYY-MM-DD' floor (today) — derives the 12-month window. */
  minDate: string
  onMonthChange: (monthStart: string) => void
}

/**
 * Shared calendar chrome: month + year dropdowns (jump anywhere in the
 * rolling 12-month window) plus ‹ › pagers, all clamped to the same window.
 * Native <select>s on purpose — the OS picker wheel on mobile beats any
 * custom dropdown here. Used by BOTH SessionCalendar and BookingCalendar so
 * the header can't diverge between booking modes.
 */
export function CalendarHeader({ monthStart, minDate, onMonthChange }: Props) {
  const win = monthWindow(minDate)
  const year = Number(monthStart.slice(0, 4))
  const monthNum = monthStart.slice(5, 7)
  const months = monthOptions(year, win)
  const years = yearOptions(win)
  const atFloor = monthStart <= win.minMonth
  const atCeil = monthStart >= win.maxMonth

  // Both controls carry a RESTING box (hairline border), not just a hover
  // state. The header previously drew nothing at rest and lit up only on
  // `hover:` — which touch devices do not have, so on mobile the month
  // stepper and the month/year jump were both invisible until pressed.
  // That matters most on narrow layouts, where BookingCalendar drops to a
  // single month (see its @2xl/cal note) and stepping is the only way to
  // reach the next one.
  //
  // Backgrounds stay transparent: this header renders inside whatever band
  // the embedding `booking_widget` section paints, so a literal fill would
  // be wrong on a dark surface. The border + the icon inherit tokens that
  // already flip with the band.
  const selectCls =
    'min-h-[44px] rounded-md border border-[var(--color-border)] bg-transparent pl-2.5 pr-1 text-base font-medium text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)]'
  // `‹` / `›` (U+2039/203A) are single angle QUOTATION MARKS — punctuation,
  // not icons. At 16px each drew ~5x8px of ink inside a 44x44 target, under
  // 3% of the button, while every neighbouring control fills its box. The
  // tap target was always correct (WCAG 2.5.5); the affordance was not, and
  // no automated check sees that. Lucide chevrons at 20px, in primary text,
  // put real ink in the same box.
  const pagerCls =
    'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md border border-[var(--color-border)] text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-border)]/40 focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'

  return (
    <div className="mb-1 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <select
          aria-label="Month"
          className={selectCls}
          value={monthStart}
          onChange={e => onMonthChange(e.target.value)}
        >
          {months.map(m => (
            <option key={m.value} value={m.value} disabled={m.disabled}>{m.label}</option>
          ))}
        </select>
        <select
          aria-label="Year"
          className={selectCls}
          value={year}
          onChange={e => onMonthChange(clampMonth(`${e.target.value}-${monthNum}-01`, win))}
        >
          {years.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button" aria-label="Previous month"
          disabled={atFloor}
          onClick={() => onMonthChange(addMonths(monthStart, -1))}
          className={pagerCls}
        ><ChevronLeft size={20} aria-hidden="true" /></button>
        <button
          type="button" aria-label="Next month"
          disabled={atCeil}
          onClick={() => onMonthChange(addMonths(monthStart, 1))}
          className={pagerCls}
        ><ChevronRight size={20} aria-hidden="true" /></button>
      </div>
    </div>
  )
}
